import cn from "classnames";
import React, { useMemo } from "react";
import { Badge } from "reactstrap";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface Props {
  formattedTotal: string;
  formattedMonthly: string;
  showMonthly: boolean;
  onToggleMonthly: () => void;
}

export const ExpectedResultBadge = ({
  formattedTotal,
  formattedMonthly,
  showMonthly,
  onToggleMonthly,
}: Props) => {
  const intl = useIntl();

  const totals = useMemo(
    () =>
      String(formattedTotal || "")
        .split(" + ")
        .map((part) => part.trim())
        .filter(Boolean),
    [formattedTotal]
  );

  const monthlies = useMemo(
    () =>
      String(formattedMonthly || "")
        .split(" + ")
        .map((part) => part.trim())
        .filter(Boolean),
    [formattedMonthly]
  );

  if (!totals.length) return null;

  return (
    <div className="crm-expected-result d-flex">
      <Badge
        className="crm-expected-result__badge text-white"
        color="success"
        title={intl.formatMessage(
          { id: "app.crmBusiness.totalProbability" },
          { probability: formattedTotal }
        )}
      >
        <span className="crm-expected-result__label">
          {intl.formatMessage({ id: "app.crmBusiness.totalProbabilityLabel" })}
        </span>
        <span className="crm-expected-result__amounts">
          {totals.map((amount, index) => (
            <span key={`${amount}-${index}`} className="crm-expected-result__amount">
              {amount}
            </span>
          ))}
        </span>
        {showMonthly && monthlies.length > 0 && (
          <span className="crm-expected-result__monthly">
            {monthlies.map((amount, index) => (
              <span
                key={`m-${amount}-${index}`}
                className="crm-expected-result__amount crm-expected-result__amount--monthly"
              >
                {amount}/mes
              </span>
            ))}
          </span>
        )}
      </Badge>
      <button
        type="button"
        className={cn("crm-expected-result__toggle", {
          "crm-expected-result__toggle--active": showMonthly,
        })}
        onClick={onToggleMonthly}
        title={intl.formatMessage({
          id: showMonthly
            ? "app.crmBusiness.hideMonthlyExpected"
            : "app.crmBusiness.showMonthlyExpected",
        })}
        aria-label={intl.formatMessage({
          id: showMonthly
            ? "app.crmBusiness.hideMonthlyExpected"
            : "app.crmBusiness.showMonthlyExpected",
        })}
        aria-pressed={showMonthly}
      >
        <FontAwesomeIcon icon={["far", "calendar-alt"]} />
      </button>
    </div>
  );
};
